import { BadgeEuro, Camera, CheckCircle2, ClipboardList, HandCoins, Send, X } from "lucide-react";
import { useEffect, useState } from "react";
import { contactDetails } from "../data/moselcars";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 8 * 1024 * 1024;
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

const steps = [
  { icon: ClipboardList, title: "Fahrzeug beschreiben", text: "Marke, Modell, Erstzulassung und Kilometerstand reichen für eine erste Einschätzung." },
  { icon: BadgeEuro, title: "Faires Angebot erhalten", text: "Wir melden uns persönlich mit einer realistischen Einschätzung – ohne Verpflichtung." },
  { icon: HandCoins, title: "Direkt vor Ort abwickeln", text: "Besichtigung, Abmeldung und Auszahlung klären wir unkompliziert in Schweich-Issel." }
];

export default function SellSection({ onOpenPrivacy }) {
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [reference, setReference] = useState("");
  const [photos, setPhotos] = useState([]);
  const [photoError, setPhotoError] = useState("");
  const [uploadProgress, setUploadProgress] = useState("");
  const [photoCountSent, setPhotoCountSent] = useState(0);

  useEffect(() => () => photos.forEach((photo) => URL.revokeObjectURL(photo.preview)), [photos]);

  const addPhotos = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    setPhotoError("");
    const accepted = [];
    for (const file of files) {
      if (photos.length + accepted.length >= MAX_PHOTOS) { setPhotoError(`Maximal ${MAX_PHOTOS} Fotos möglich.`); break; }
      if (!PHOTO_TYPES.includes(file.type)) { setPhotoError("Bitte nur Fotos im Format JPG, PNG, WEBP oder HEIC auswählen."); continue; }
      if (file.size > MAX_PHOTO_BYTES) { setPhotoError(`„${file.name}“ ist größer als 8 MB.`); continue; }
      accepted.push({ id: `${file.name}-${file.size}-${file.lastModified}`, file, preview: URL.createObjectURL(file) });
    }
    if (accepted.length) setPhotos((current) => [...current, ...accepted]);
  };

  const removePhoto = (id) => setPhotos((current) => current.filter((photo) => photo.id !== id));

  const uploadPhotos = async () => {
    const ids = [];
    for (const [index, photo] of photos.entries()) {
      setUploadProgress(`Foto ${index + 1} von ${photos.length} wird hochgeladen …`);
      const body = new FormData();
      body.append("file", photo.file, photo.file.name);
      const response = await fetch(`${BACKEND_URL}/api/uploads/ankauf`, { method: "POST", body });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || `Foto „${photo.file.name}“ konnte nicht hochgeladen werden.`);
      ids.push(payload.id);
    }
    setUploadProgress("");
    return ids;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const vehicle = `Ankauf: ${data.get("car")}`.trim();
    const facts = [data.get("registration") && `EZ ${data.get("registration")}`, data.get("mileage") && `${data.get("mileage")} km`, data.get("price") && `Preisvorstellung ${data.get("price")} €`].filter(Boolean).join(" · ");
    const message = `${facts ? `Fahrzeugdaten: ${facts}\n\n` : ""}${String(data.get("message") || "").trim() || "Bitte um ein Ankaufangebot."}`;
    setStatus("sending");
    setErrorMessage("");
    try {
      const photo_ids = await uploadPhotos();
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inquiry_type: "ankauf", photo_ids, name: String(data.get("name") || "").trim(), email: String(data.get("email") || "").trim(), phone: String(data.get("phone") || "").trim(), vehicle, message, consent: data.get("consent") === "on" })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Die Anfrage konnte nicht gesendet werden.");
      setReference(payload.reference);
      setPhotoCountSent(photo_ids.length);
      setStatus("success");
      setPhotos([]);
      form.reset();
    } catch (error) {
      setUploadProgress("");
      setErrorMessage(error.message);
      setStatus("error");
    }
  };

  return (
    <section className="sell-section section" id="ankauf" aria-labelledby="sell-title" data-testid="sell-section">
      <div className="page-shell sell-layout">
        <Reveal className="sell-copy">
          <span className="section-kicker">03 / FAHRZEUGANKAUF</span>
          <h2 id="sell-title">Wir kaufen<br /><em>Ihr Auto.</em></h2>
          <p>Sie möchten Ihren Gebrauchtwagen verkaufen oder in Zahlung geben? Mosel Cars kauft Fahrzeuge aller Marken an – fair bewertet, schnell abgewickelt, direkt in Schweich-Issel.</p>
          <ol className="sell-steps">
            {steps.map((step, index) => <li key={step.title} data-testid={`sell-step-${index + 1}`}><step.icon size={20} strokeWidth={1.5} /><div><strong>{step.title}</strong><span>{step.text}</span></div></li>)}
          </ol>
        </Reveal>
        <Reveal className="sell-form-wrap" delay="100ms">
          {status === "success" ? (
            <div className="confirmation-card" data-testid="sell-confirmation-card">
              <CheckCircle2 size={28} />
              <h3>Ankaufanfrage erhalten</h3>
              <p>Ihre Fahrzeugdaten{reference && photoCountSent ? ` und ${photoCountSent} Foto${photoCountSent === 1 ? "" : "s"}` : ""} sind bei Mosel Cars ({contactDetails.email}) eingegangen. Wir melden uns persönlich mit einer Einschätzung.</p>
              <span className="mono-label">Referenz <strong data-testid="sell-reference">{reference}</strong></span>
              <button className="text-action" type="button" onClick={() => setStatus("idle")} data-testid="sell-new-request-button">Weiteres Fahrzeug anbieten</button>
            </div>
          ) : (
            <form className="contact-form sell-form" onSubmit={handleSubmit} noValidate={false} data-testid="sell-form">
              <div className="form-row">
                <label htmlFor="sell-car">Marke &amp; Modell<input id="sell-car" name="car" type="text" placeholder="z. B. BMW 320d Touring" required minLength={2} maxLength={120} data-testid="sell-car-input" /></label>
                <label htmlFor="sell-registration">Erstzulassung<input id="sell-registration" name="registration" type="text" inputMode="numeric" placeholder="z. B. 03/2016" maxLength={12} data-testid="sell-registration-input" /></label>
              </div>
              <div className="form-row">
                <label htmlFor="sell-mileage">Kilometerstand<input id="sell-mileage" name="mileage" type="number" inputMode="numeric" min={0} max={2000000} step={1000} placeholder="z. B. 120000" data-testid="sell-mileage-input" /></label>
                <label htmlFor="sell-price">Preisvorstellung in € (optional)<input id="sell-price" name="price" type="number" inputMode="numeric" min={0} max={1000000} step={100} placeholder="z. B. 9000" data-testid="sell-price-input" /></label>
              </div>
              <div className="form-row">
                <label htmlFor="sell-name">Name<input id="sell-name" name="name" type="text" autoComplete="name" required minLength={2} maxLength={120} placeholder="Ihr Name" data-testid="sell-name-input" /></label>
                <label htmlFor="sell-email">E-Mail-Adresse<input id="sell-email" name="email" type="email" autoComplete="email" required placeholder="name@beispiel.de" data-testid="sell-email-input" /></label>
              </div>
              <label htmlFor="sell-phone">Telefonnummer (optional)<input id="sell-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="z. B. 0651 1234567" maxLength={40} pattern="[0-9+\(\)\.\/ \-]{5,40}" title="Bitte eine gültige Telefonnummer eingeben" data-testid="sell-phone-input" /></label>
              <label htmlFor="sell-message">Zustand &amp; Besonderheiten<textarea id="sell-message" name="message" rows={4} maxLength={2000} placeholder="z. B. unfallfrei, Scheckheft gepflegt, zweiter Satz Reifen …" data-testid="sell-message-input" /></label>
              <div className="photo-field" data-testid="sell-photo-field">
                <span className="photo-field-label">Fotos (optional, bis zu {MAX_PHOTOS})</span>
                <div className="photo-grid">
                  {photos.map((photo) => <div className="photo-thumb" key={photo.id} data-testid="sell-photo-thumb"><img src={photo.preview} alt={photo.file.name} /><button type="button" aria-label={`${photo.file.name} entfernen`} onClick={() => removePhoto(photo.id)} data-testid="sell-photo-remove-button"><X size={13} /></button></div>)}
                  {photos.length < MAX_PHOTOS && <label className="photo-add" htmlFor="sell-photos" data-testid="sell-photo-add-label"><Camera size={20} strokeWidth={1.5} /><span>{photos.length ? "Weiteres Foto" : "Fotos auswählen"}</span><input id="sell-photos" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple onChange={addPhotos} data-testid="sell-photo-input" /></label>}
                </div>
                <small>Front, Seite, Heck und Innenraum helfen bei der Einschätzung. JPG, PNG, WEBP oder HEIC, je max. 8 MB.</small>
                {photoError && <p className="contact-error" role="alert" data-testid="sell-photo-error">{photoError}</p>}
              </div>
              <label className="consent-field" htmlFor="sell-consent"><input id="sell-consent" name="consent" type="checkbox" required data-testid="sell-consent-input" /><span>Ich stimme zu, dass Mosel Cars meine Angaben zur Bearbeitung dieser Ankaufanfrage per E-Mail verarbeitet. <a href="/datenschutz" onClick={(event) => { event.preventDefault(); onOpenPrivacy(); }} data-testid="sell-privacy-link">Datenschutz</a></span></label>
              {status === "error" && <p className="contact-error" role="alert" data-testid="sell-error-message">{errorMessage}</p>}
              <button className="button button-primary" type="submit" disabled={status === "sending"} data-testid="sell-submit-button"><Send size={16} /> {status === "sending" ? (uploadProgress || "Wird gesendet …") : "Ankaufangebot anfragen"}</button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
